import { IPE_PLUGIN_CHROME_STORE_URL, IPE_SUPPORT_CONFIG } from '@/config/ipeSupportConfig';

export const IPE_PLUGIN_EXTENSION_ID = 'ajlohndkojofcoaeofeajbagipipldbb';

/** @typedef {'chrome' | 'edge' | 'brave' | 'opera' | 'firefox' | 'safari' | 'other'} PluginBrowserKind */

/** @returns {PluginBrowserKind} */
export function detectPluginBrowserKind() {
  if (typeof navigator === 'undefined') return 'other';
  const ua = navigator.userAgent || '';

  if (/Edg\//.test(ua)) return 'edge';
  if (/OPR\//.test(ua) || /Opera/.test(ua)) return 'opera';
  if (/Brave/.test(ua)) return 'brave';
  if (/Firefox\//.test(ua)) return 'firefox';
  if (/Safari\//.test(ua) && !/Chrome\//.test(ua) && !/CriOS\//.test(ua)) return 'safari';
  if (/Chrome\//.test(ua) || /CriOS\//.test(ua)) return 'chrome';
  return 'other';
}

function buildChromeStoreUrl(browserKind) {
  const configured = IPE_SUPPORT_CONFIG.pluginChromeStoreUrl || IPE_PLUGIN_CHROME_STORE_URL;
  try {
    const url = new URL(configured);
    url.searchParams.set('utm_source', `ipe_workbench_${browserKind}`);
    return url.toString();
  } catch {
    return configured;
  }
}

/**
 * 文案 key 对应 workbench.pluginInstallPage.*；浏览器品牌名保持原文。
 * @type {Record<PluginBrowserKind, {
 *   browserLabel: string;
 *   storeLabelKey: string | null;
 *   buttonTextKey: string;
 *   installActionKey: string | null;
 *   supported: boolean;
 *   needsProxy: boolean;
 *   storeHintKey: string | null;
 * }>}
 */
const BROWSER_PROFILES = {
  chrome: {
    browserLabel: 'Google Chrome',
    storeLabelKey: 'storeChrome',
    buttonTextKey: 'btnChrome',
    installActionKey: 'actionAddChrome',
    supported: true,
    needsProxy: true,
    storeHintKey: null,
  },
  edge: {
    browserLabel: 'Microsoft Edge',
    storeLabelKey: 'storeChrome',
    buttonTextKey: 'btnStore',
    installActionKey: 'actionAddEdge',
    supported: true,
    needsProxy: true,
    storeHintKey: 'hintEdge',
  },
  brave: {
    browserLabel: 'Brave',
    storeLabelKey: 'storeChrome',
    buttonTextKey: 'btnChrome',
    installActionKey: 'actionAddBrave',
    supported: true,
    needsProxy: true,
    storeHintKey: null,
  },
  opera: {
    browserLabel: 'Opera',
    storeLabelKey: 'storeChrome',
    buttonTextKey: 'btnChrome',
    installActionKey: 'actionAddOpera',
    supported: true,
    needsProxy: true,
    storeHintKey: null,
  },
  firefox: {
    browserLabel: 'Firefox',
    storeLabelKey: null,
    buttonTextKey: 'btnUseChromeEdge',
    installActionKey: null,
    supported: false,
    needsProxy: false,
    storeHintKey: 'hintUnsupported',
  },
  safari: {
    browserLabel: 'Safari',
    storeLabelKey: null,
    buttonTextKey: 'btnUseChromeEdge',
    installActionKey: null,
    supported: false,
    needsProxy: false,
    storeHintKey: 'hintUnsupported',
  },
  other: {
    browserLabelKey: 'browserOther',
    storeLabelKey: 'storeChrome',
    buttonTextKey: 'btnStore',
    installActionKey: 'actionAddBrowser',
    supported: true,
    needsProxy: true,
    storeHintKey: 'hintOther',
  },
};

/**
 * 根据当前浏览器返回插件商店安装目标（文案 key，由页面 t() 解析）。
 * @returns {{
 *   kind: PluginBrowserKind;
 *   browserLabel: string | null;
 *   browserLabelKey: string | null;
 *   storeLabelKey: string | null;
 *   buttonTextKey: string;
 *   installActionKey: string | null;
 *   supported: boolean;
 *   needsProxy: boolean;
 *   storeHintKey: string | null;
 *   storeUrl: string;
 *   offlineUrl: string;
 * }}
 */
export function resolvePluginStoreTarget() {
  const kind = detectPluginBrowserKind();
  const profile = BROWSER_PROFILES[kind] || BROWSER_PROFILES.other;
  const storeUrl = profile.supported ? buildChromeStoreUrl(kind) : '';
  const offlineUrl = IPE_SUPPORT_CONFIG.pluginDownloadUrl || '';

  return {
    kind,
    browserLabel: profile.browserLabel || null,
    browserLabelKey: profile.browserLabelKey || null,
    storeLabelKey: profile.storeLabelKey,
    buttonTextKey: profile.buttonTextKey,
    installActionKey: profile.installActionKey,
    supported: profile.supported,
    needsProxy: profile.needsProxy,
    storeHintKey: profile.storeHintKey,
    storeUrl,
    offlineUrl,
  };
}

/** @deprecated 使用 resolvePluginStoreTarget().storeUrl */
export function resolvePluginInstallUrlForBrowser() {
  const target = resolvePluginStoreTarget();
  return target.storeUrl || target.offlineUrl || '';
}
